import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { useCMSStore } from "@/hooks/use-cms-store";

import { useCMSTabContext } from "../cms-tab-context";
import { useUniqueTabContext } from "./unique-tab-context";

const cmsUniqueTabSettingsFormSchema = z.object({
  show_header_title: z.boolean(),
  show_header_description: z.boolean(),
  sync_with_tamper_status: z.boolean(),
});

export const UniqueTabSettings = () => {
  const { tabIndex } = useCMSTabContext();
  const { options } = useUniqueTabContext();
  const { onUpdateUniqueTabOptions } = useCMSStore();

  const form = useForm({
    resolver: zodResolver(cmsUniqueTabSettingsFormSchema),
    defaultValues: {
      show_header_title: options.show_header_title,
      show_header_description: options.show_header_description,
      sync_with_tamper_status: options.sync_with_tamper_status,
    },
    mode: "onChange",
  });

  useEffect(() => {
    const unsubscribe = form.subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        onUpdateUniqueTabOptions(tabIndex, { ...options, ...values });
      },
    });

    return unsubscribe;
  }, [form, onUpdateUniqueTabOptions, options, tabIndex]);

  return (
    <Card>
      <CardHeader>
        <h4 className="text-lg font-medium leading-none">Settings</h4>
      </CardHeader>
      <Form {...form}>
        <CardContent className="text-sm space-y-4">
          <FormField
            control={form.control}
            name="show_header_title"
            render={({ field }) => (
              <FormItem className="flex justify-between items-center">
                <FormLabel>Show Header Title</FormLabel>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="show_header_description"
            render={({ field }) => (
              <FormItem className="flex justify-between items-center">
                <FormLabel>Show Header Description</FormLabel>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="sync_with_tamper_status"
            render={({ field }) => (
              <FormItem className="flex justify-between items-center">
                <div className="space-y-1.5">
                  <FormLabel>Sync with Tamper Tag</FormLabel>
                  <FormDescription>
                    Lock the contents of this tab until a consumer breaks the
                    seal of the tamper tag.
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </CardContent>
      </Form>
    </Card>
  );
};
